"use client";

import { Suspense, useEffect, useMemo, type ReactNode } from "react";
import {
  useRouter as useAppRouter,
  usePathname,
  useSearchParams,
} from "next/navigation";
import { RouterFuncsCtx, type RouterFunctions } from "./context";
import { routerEvents, setSnapshot, parseSearchToQuery } from "./router-store";

type UrlInput =
  | string
  | {
      pathname?: string;
      query?: Record<string, string | number | (string | number)[] | undefined>;
      hash?: string;
    };

function resolveUrl(url: UrlInput): string {
  if (typeof url === "string") return url;
  const { pathname = "", query = {}, hash = "" } = url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });
  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
}

/**
 * Keeps the shared store in sync with the current App Router URL. Rendered
 * inside its own Suspense boundary so useSearchParams never triggers a CSR
 * bailout for statically prerendered pages.
 */
function UrlBridge() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const search = searchParams?.toString() ?? "";
    const asPath = `${pathname ?? ""}${search ? `?${search}` : ""}`;
    setSnapshot({
      pathname: pathname ?? "",
      asPath,
      query: parseSearchToQuery(search),
      isReady: true,
    });
    routerEvents.emit("routeChangeComplete", asPath, { shallow: false });
  }, [pathname, searchParams]);

  return null;
}

/**
 * App Router provider. Supplies next/navigation navigation functions and keeps
 * the shared router store updated from the current pathname/search params.
 */
export function AppCompatRouterProvider({ children }: { children: ReactNode }) {
  const router = useAppRouter();
  const pathname = usePathname();

  const value = useMemo<RouterFunctions>(
    () =>
      ({
        push: async (url: UrlInput) => {
          await router.push(resolveUrl(url));
          return true;
        },
        replace: async (url: UrlInput) => {
          await router.replace(resolveUrl(url));
          return true;
        },
        reload: () => {
          window.location.reload();
        },
        prefetch: async (url: UrlInput) => {
          router.prefetch(resolveUrl(url));
        },
        back: () => {
          router.back();
        },
        events: routerEvents,
        staticRouter: {
          pathname: pathname ?? "",
          asPath: pathname ?? "",
          query: {},
          isReady: false,
        },
      }) as unknown as RouterFunctions,
    [router, pathname],
  );

  return (
    <RouterFuncsCtx.Provider value={value}>
      <Suspense fallback={null}>
        <UrlBridge />
      </Suspense>
      {children}
    </RouterFuncsCtx.Provider>
  );
}
